import React, { useState, useEffect } from 'react';
import {
    Box,
    Heading,
    Text,
    VStack,
    HStack,
    FormControl,
    FormLabel,
    Input,
    Select,
    Radio,
    RadioGroup,
    Flex,
    Tag,
    useDisclosure,
} from '@chakra-ui/react';
import EmailPreviewModal from '../../../TemplateManager/CreateTemplate/EmailPreviewModal';
import { isHtmlBody } from '../../../TemplateManager/CreateTemplate/ChannelPreviewBox';
import { ChevronDownIcon } from '@chakra-ui/icons';

const TemplateVariableConfig = ({ setFormData, formData }) => {
    const { isOpen: isEmailPreviewOpen, onOpen: onEmailPreviewOpen, onClose: onEmailPreviewClose } = useDisclosure();
    const [variableConfig, setVariableConfig] = useState({});
    const [availableDynamicFields, setAvailableDynamicFields] = useState([]);
    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        const formatted = [{
            key: "Customer Name",
            value: "$cust_name".replace('$', ''), // remove `$` for consistency
            description: "Customer Name"
        }]

        setAvailableDynamicFields(formatted);
    }, []);

    // Parse message_combine JSON string and return variables array
    const getParsedMessageCombine = () => {
        try {
            const raw = formData.selectedTemplate?.message_combine;
            if (!raw) return null;
            return typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch {
            return null;
        }
    };

    // Check if IVR (Voice) is among the selected channels
    const hasIVRChannel = () => {
        const parsed = getParsedMessageCombine();
        const channels = parsed?.selectedChannels || [];
        return channels.some(ch => ch === 'Voice (IVR)' || ch?.toLowerCase?.()?.includes('ivr'));
    };

    // Extract IVR-specific variables from ivr_variables, stripping the "variables." prefix
    const extractIVRVariableNames = () => {
        const parsed = getParsedMessageCombine();
        const ivrVars = parsed?.ivr_variables;
        if (!Array.isArray(ivrVars) || ivrVars.length === 0) return [];
        return ivrVars.map(v => {
            const parts = v.split('.');
            return parts.length > 1 ? parts.slice(1).join('.') : v;
        }).filter(Boolean);
    };

    // Extract variables — combines standard variables[] with ivr_variables[] when IVR is selected
    const extractVariables = () => {
        const parsed = getParsedMessageCombine();

        // Standard variables from variables[]
        const variablesArr = parsed?.variables;
        const standardVars = (variablesArr && Array.isArray(variablesArr) && variablesArr.length > 0)
            ? variablesArr.map(v => v.name).filter(Boolean)
            : [];

        // IVR variables (only if IVR channel is selected)
        const ivrVars = hasIVRChannel() ? extractIVRVariableNames() : [];

        // Merge and deduplicate (standard first, IVR appended)
        return [...new Set([...standardVars, ...ivrVars])];
    };

    // Returns true if a variable name came from ivr_variables (not standard variables[])
    const isIVRVariable = (varName) => {
        const parsed = getParsedMessageCombine();
        const standardNames = (parsed?.variables || []).map(v => v.name);
        return !standardNames.includes(varName) && extractIVRVariableNames().includes(varName);
    };
    // Initialize variable configuration - handles both new and edit scenarios
    useEffect(() => {
        if (formData.selectedTemplate?.message_combine) {
            const variables = extractVariables();

            if (variables.length === 0) {
                setVariableConfig({});
                return;
            }

            const newConfig = {};
            let shouldInitialize = false;

            variables.forEach(variable => {
                // Check if we already have this variable configured
                if (variableConfig[variable]) {
                    // Keep existing configuration
                    newConfig[variable] = variableConfig[variable];
                } else {
                    // New variable needs configuration
                    shouldInitialize = true;

                    // Check if there's existing data in formData.template_variables (edit mode)
                    const existingConfig = formData.template_variables?.[variable];

                    if (existingConfig) {
                        // Pre-populate from existing template_variables
                        if (existingConfig.type === 'fixed') {
                            newConfig[variable] = {
                                type: 'fixed',
                                value: existingConfig.value || '',
                                dynamicField: '',
                                alternateValue: ''
                            };
                        } else if (existingConfig.type === 'dynamic') {
                            // Extract field name from value (remove $)
                            const fieldName = existingConfig.value ? existingConfig.value.replace('$', '') : '';
                            newConfig[variable] = {
                                type: 'dynamic',
                                value: '',
                                dynamicField: fieldName,
                                alternateValue: existingConfig.alternate_value || ''
                            };
                        }
                    } else {
                        // Default configuration for new variables
                        newConfig[variable] = {
                            type: 'fixed',
                            value: '',
                            dynamicField: '',
                            alternateValue: ''
                        };
                    }
                }
            });

            // Update configuration if needed
            if (shouldInitialize || Object.keys(variableConfig).length === 0) {
                setVariableConfig(newConfig);
                if (!hasInitialized) {
                    setHasInitialized(true);
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.selectedTemplate?.message_combine, formData.template_variables]);



    // Handle variable configuration change
    const handleVariableChange = (variableName, field, value) => {
        setVariableConfig(prev => ({
            ...prev,
            [variableName]: {
                ...prev[variableName],
                [field]: value,
                // Clear the other field when switching types
                ...(field === 'type' && value === 'fixed' ? { dynamicField: '', alternateValue: '' } : {}),
                ...(field === 'type' && value === 'dynamic' ? { value: '' } : {})
            }
        }));
    };

    // Generate template_variables object in the required format
    const generateTemplateVariables = () => {
        const templateVariables = {};

        Object.keys(variableConfig).forEach(variableName => {
            const config = variableConfig[variableName];

            if (config) {
                if (config.type === 'fixed' && config.value) {
                    templateVariables[variableName] = {
                        value: config.value,
                        type: 'fixed'
                    };
                } else if (config.type === 'dynamic' && config.dynamicField) {
                    templateVariables[variableName] = {
                        value: `$${config.dynamicField}`,
                        type: 'dynamic',
                        alternate_value: config.alternateValue || ''
                    };
                }
            }
        });

        return templateVariables;
    };

    // Update formData with template_variables whenever variableConfig changes
    useEffect(() => {
        if (Object.keys(variableConfig).length > 0) {
            const templateVariables = generateTemplateVariables();

            // Always update formData with current configuration
            setFormData(prev => ({
                ...prev,
                template_variables: templateVariables
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variableConfig, setFormData]);

    // Generate preview message
    const generatePreviewMessage = () => {
        if (!formData.selectedTemplate?.message) return '';

        let preview = formData.selectedTemplate.message;
        const variables = extractVariables();

        variables.forEach(variable => {
            const config = variableConfig[variable];
            let replacement = `{{${variable}}}`;

            if (config) {
                if (config.type === 'fixed' && config.value) {
                    replacement = config.value;
                } else if (config.type === 'dynamic' && config.dynamicField) {
                    replacement = `{{${config.dynamicField}}}`;
                }
            }

            preview = preview.replace(new RegExp(`\\$${variable}`, 'g'), replacement);
        });

        return preview;
    };

    const variables = extractVariables();
    const parsedMessageCombine = getParsedMessageCombine();
    const checkAlternate = (data) => {
        if (!data || typeof data !== "object") return false; // prevent crash
        return Object.values(data).some((item) => item?.hasOwnProperty("alternate_value"));
    };

    const hasAlternate = checkAlternate(formData?.template_variables);

    return (
        <Box>
            {/* <Card> */}
            <Box>
                <Text fontSize="2xl" fontWeight="600">
                    Configure Template Variables
                </Text>
                <Text fontSize="sm" color="gray.600" mt={2}>
                    For each variable, choose whether to use a fixed value or dynamic customer data. Dynamic values will be automatically populated from customer information.
                </Text>
            </Box>

            <Box  >
                <Flex gap={6}>
                    {/* Variable Configuration Panel */}
                    <Box flex="1">
                        <Text fontSize="2xl" fontWeight="500" mt={4}>Variable Configuration</Text>

                        {variables.length === 0 ? (
                            <Box textAlign="center" py={8} color="gray.500">
                                No variables found in template message
                            </Box>
                        ) : (
                            <VStack spacing={6} align="stretch">
                                {variables.map((variable, index) => {
                                    const variableMeta = parsedMessageCombine?.variables?.find(v => v.name === variable);
                                    return (
                                    <Box key={variable} border={`1px solid #0000001a`} size="sm" borderRadius={"15px"} p={4}>
                                        <Box>
                                            <Flex align="center" justify="space-between" mb={3}>
                                                <Box>
                                                    <HStack spacing={2} mb={0}>
                                                        <FormLabel fontSize="sm" fontWeight="medium" mb={0}>
                                                            {variable}
                                                        </FormLabel>
                                                        {isIVRVariable(variable) && (
                                                            <Tag size="sm" bg="var(--brand-tint)" color="var(--brand-primary)" fontSize="xs" borderRadius="full" px={2} py={0.5}>
                                                                Voice (IVR)
                                                            </Tag>
                                                        )}
                                                    </HStack>
                                                    {variableMeta?.description && (
                                                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                                                            {variableMeta.description}
                                                        </Text>
                                                    )}
                                                </Box>
                                                {/* <HStack>
                                                        <Tag
                                                            bg={variableConfig[variable]?.type === 'fixed' ? 'blue' : 'green'}
                                                            fontSize="xs"
                                                            color={"white"}
                                                        >
                                                            {variableConfig[variable]?.type === 'fixed' ? 'Fixed Value' : 'Dynamic Value'}
                                                        </Tag>
                                                        <Icon as={InfoIcon} w={4} h={4} color="gray.400" />
                                                    </HStack> */}
                                            </Flex>

                                            {/* Type Selection */}
                                            <FormControl mb={3}>
                                                <RadioGroup
                                                    value={variableConfig[variable]?.type || 'fixed'}
                                                    onChange={(value) => handleVariableChange(variable, 'type', value)}
                                                >
                                                    <HStack spacing={6}>
                                                        <Radio value="fixed">Fixed Value</Radio>
                                                        <Radio value="dynamic" isDisabled={hasAlternate}>Dynamic Value</Radio>
                                                    </HStack>
                                                </RadioGroup>
                                            </FormControl>

                                            {/* Value Input */}
                                            {variableConfig[variable]?.type === 'fixed' ? (
                                                <FormControl>
                                                    <Input
                                                        placeholder={`Enter ${variable}`}
                                                        value={variableConfig[variable]?.value || ''}
                                                        onChange={(e) => handleVariableChange(variable, 'value', e.target.value)}
                                                        bg="white"
                                                    />
                                                    <Text fontSize="xs" color="var(--brand-primary)" mt={1}>
                                                        This fixed value will be used for all recipients
                                                    </Text>
                                                </FormControl>
                                            ) : (
                                                <VStack spacing={3} align="stretch">
                                                    <FormControl>
                                                        <FormLabel fontSize="xs" color="gray.600" mb={1}>
                                                            Dynamic Field
                                                        </FormLabel>
                                                        <Select
                                                            placeholder="Select dynamic field..."
                                                            value={variableConfig[variable]?.dynamicField || ''}
                                                            onChange={(e) => handleVariableChange(variable, 'dynamicField', e.target.value)}
                                                            bg="white"
                                                            icon={<ChevronDownIcon />}
                                                        >
                                                            {availableDynamicFields.map((field) => (
                                                                <option key={field.value} value={field.value}>
                                                                    {field.key}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                        <Text fontSize="xs" color="blue.600" mt={1}>
                                                            This value will be automatically filled from customer data during send
                                                        </Text>
                                                    </FormControl>

                                                    <FormControl>
                                                        <FormLabel fontSize="xs" color="gray.600" mb={1}>
                                                            Alternate Value (Optional)
                                                        </FormLabel>
                                                        <Input
                                                            placeholder={`Fallback value if ${variable} is empty`}
                                                            value={variableConfig[variable]?.alternateValue || ''}
                                                            onChange={(e) => handleVariableChange(variable, 'alternateValue', e.target.value)}
                                                            bg="white"
                                                            size="sm"
                                                        />
                                                        <Text fontSize="xs" color="orange.300" mt={1}>
                                                            This value will be used when customer data is not available
                                                        </Text>
                                                    </FormControl>
                                                </VStack>
                                            )}
                                        </Box>
                                    </Box>
                                    );
                                })}
                            </VStack>
                        )}
                    </Box>

                    {/* Message Preview Panel */}
                    <Box w="400px" pl={6}>




                        <Box className='box-style-card' mb={4}>
                            <Heading size="sm" mb={4}>Message Preview</Heading>
                            {/* An email template's body is HTML — printing it in a <Text>
                                showed the raw source. Offer the rendered preview instead. */}
                            {isHtmlBody(generatePreviewMessage(), formData.selectedTemplate?.emailType) ? (
                                <>
                                    <Text
                                        as="button" type="button" textAlign="left"
                                        fontSize="sm" fontWeight="600" cursor="pointer"
                                        color="var(--brand-primary)" textDecoration="underline"
                                        _hover={{ color: 'var(--brand-primary-hover)' }}
                                        onClick={onEmailPreviewOpen}
                                    >
                                        Preview Email
                                    </Text>
                                    <Text fontSize="xs" color="gray.500" mt={2}>
                                        Opens the rendered email with your variable values applied.
                                    </Text>
                                    <EmailPreviewModal
                                        subject={formData.selectedTemplate?.emailSubject}
                                        body={generatePreviewMessage()}
                                        isEmailPreviewOpen={isEmailPreviewOpen}
                                        onEmailPreviewClose={onEmailPreviewClose}
                                    />
                                </>
                            ) : (
                                <Text fontSize="sm" whiteSpace="pre-wrap">
                                    {generatePreviewMessage()}
                                </Text>
                            )}
                        </Box>

                        <VStack align="stretch" spacing={3} className='box-style-card'>
                            <Heading size="sm" color="gray.700">Preview Notes:</Heading>
                            <VStack align="stretch" spacing={1} fontSize="xs" color="gray.600">
                                <Text>• Fixed values appear as entered</Text>
                                <Text>• Dynamic values show as [Field Name] in preview</Text>
                                <Text>• Actual values will be populated from customer data during send</Text>
                                <Text>• Empty variables appear as placeholders</Text>
                            </VStack>
                        </VStack>

                    </Box>
                </Flex>
            </Box>

        </Box>
    );
};

export default TemplateVariableConfig;